import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import { useTranslationApp } from "../../../../lib/i18next";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetSavedAudiences } from "../../../../actions/adsCampaigns";
import _ from "lodash";
import { useDebounced } from "../../../../hooks/useDebounce";
import {
  BORDER_RADIUS,
  DELAY_DEBOUNCE,
  META,
} from "../../../../utils/constants";
import ModalBasicLayout from "../../../Modal/ModalBasicLayout";
import Button from "../../../Buttons/Button";
import {
  BUTTON_GO_BACK,
  BUTTON_SAVE,
  BUTTON_UPDATE,
  EMPTY_RESULTS_SHOW,
  LOADING_INFORMATION,
  MODAL_SAVED_AUDIENCES_DESCRIPTION,
  MODAL_SAVED_AUDIENCES_TITLE,
  SEARCH_BY_NAME_PLACEHOLDER,
  UPDATING_INFORMATION,
} from "../../../../i18n/keysTranslations";
import TextField from "../../../Form/TextField";
import Loader from "../../../Loaders/Loader";
import { DialogActions } from "@mui/material";
import ContainerModalActions from "../../../Containers/ContainerModalActions";
import Box from "@mui/material/Box";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";

const platform = META;

const ModalSavedAudiencesMeta = ({
  modalOpen,
  onCloseModal,
  onCallbackSave,
  integrationID,
  adAccountID,
}) => {
  const { control, watch } = useForm({
    defaultValues: {
      name: "",
    },
  });

  const name = watch("name");

  const [loadLastItem, onChangeLoadLastItem] = useState(false);
  const [lastKey, onChangeLastKey] = useState(undefined);
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const { ref: fromRef, inView: isNearScreen } = useInView({
    triggerOnce: false,
    rootMargin: "20px 0px",
  });

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const fetchSavedAudiences = async ({
    searchName = name,
    shouldReset = false,
    isUpdate = false,
  }) => {
    if (isFetching) return false;

    try {
      setIsFetching(true);
      if (isUpdate) {
        setIsUpdating(true);
      }
      if (shouldReset) {
        setIsLoading(true);
      }

      const response = await dispatch(
        startGetSavedAudiences({
          platform,
          integrationID,
          adAccountID,
          lastKey: shouldReset ? undefined : lastKey,
          onChangeLastKey,
          onChangeLoadLastItem,
          name: searchName,
        })
      );

      if (response.ok) {
        const data = response.data;

        setSavedAudiences((prev) =>
          shouldReset ? data : _.uniqBy([...prev, ...data], "id")
        );
      }

      setIsLoading(false);
      if (isUpdate) {
        setIsUpdating(false);
      }

      return response.ok;
    } finally {
      setIsFetching(false);
    }
  };

  const onGetSavedAudiences = async () => {
    if (loadLastItem) return;
    return fetchSavedAudiences({ searchName: "" });
  };

  const onGetSavedAudiencesByName = useDebounced(
    async (nameProp) => {
      if (!nameProp) {
        onChangeLoadLastItem(false);
        onChangeLastKey(undefined);
        return fetchSavedAudiences({
          shouldReset: true,
          searchName: "",
          forcedLoadLastItem: false,
          forcedLastKey: undefined,
        });
      }

      onChangeLoadLastItem(true);
      return fetchSavedAudiences({ searchName: nameProp, shouldReset: true });
    },
    [DELAY_DEBOUNCE]
  );

  const onUpdateSavedAudiences = async () => {
    return fetchSavedAudiences({ shouldReset: true, isUpdate: true });
  };
  const onToggle = (item) => {
    setSelected(item);
  };

  useEffect(() => {
    if (!name) {
      onGetSavedAudiences();
    }
    // eslint-disable-next-line
  }, [isNearScreen]);

  const optionsFiltered = name
    ? savedAudiences.filter((v) =>
        v.name.toLowerCase().includes(name.toLowerCase())
      )
    : savedAudiences;

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={async () => {
        onCloseModal();
      }}
      maxWidth="sm"
      scroll="paper"
      title={t(MODAL_SAVED_AUDIENCES_TITLE)}
      description={t(MODAL_SAVED_AUDIENCES_DESCRIPTION)}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 1,
          justifyContent: "flex-end",
          width: "100%",
          mt: 3.5,
          mb: 2,
        }}
      >
        <Button
          disabled={isLoading}
          color="secondary"
          startIcon={<CachedIcon />}
          onClick={onUpdateSavedAudiences}
          sx={{
            width: "50%",
          }}
        >
          {t(BUTTON_UPDATE)}
        </Button>
      </Box>

      <TextField
        name="name"
        variant="filled"
        fullWidth={true}
        placeholder={t(SEARCH_BY_NAME_PLACEHOLDER)}
        startIcon={<SearchIcon sx={{ color: "#999999 !important" }} />}
        actionsChange={(name) => {
          setIsLoading(true);
          onGetSavedAudiencesByName(name);
        }}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            height: "35px",
          },
          "& .MuiInputBase-input": {
            padding: "6px 12px",
          },
        }}
        control={control}
      />

      {isLoading && (
        <Loader
          hasMessage={true}
          message={t(isUpdating ? UPDATING_INFORMATION : LOADING_INFORMATION)}
        />
      )}

      {optionsFiltered.length === 0 && !isLoading ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
          {t(EMPTY_RESULTS_SHOW)}
        </Typography>
      ) : null}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {!isLoading &&
          optionsFiltered.map((option, index) => {
            const isSelected = selected?.id === option.id;
            return (
              <>
                <Box
                  key={option.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    flexDirection: "row",
                    gap: 1,
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    borderRadius: BORDER_RADIUS,
                    p: 1.2,
                    width: "100%",
                    minHeight: 72,
                    cursor: "pointer",
                  }}
                  role="button"
                  onClick={() => {
                    onToggle(option);
                  }}
                >
                  <Radio
                    checked={isSelected}
                    onChange={() => onToggle(option)}
                    value={option.id}
                    sx={{
                      p: 0,
                      "&.Mui-checked": {
                        color: isSelected
                          ? "primary.contrastText"
                          : "primary.main",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        mt: 0.25,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                </Box>
                {index === optionsFiltered.length - 1 && (
                  <>
                    {!loadLastItem && <Loader size={20} />}
                    <div ref={fromRef} />
                  </>
                )}
              </>
            );
          })}
      </Box>

      <DialogActions
        sx={{
          p: 0,
        }}
      >
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_GO_BACK,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: !selected,
            text: BUTTON_SAVE,
            onClick: async () => {
              onCallbackSave(selected);
            },
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalSavedAudiencesMeta;
